import React, { useState } from 'react';
import { Group, Select, NumberInput, ActionIcon, Text, Tooltip, Paper, SelectProps } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import ClickableHelpText from './ClickableHelpText';

const PROVIDERS = [
  'raydium', 'meteora', 'PumpFun'
];

export interface PoolProvidersState {
  [provider: string]: { min: number | null, max: number | null };
}

interface PoolProvidersFilterProps {
  value: PoolProvidersState;
  onChange: (value: PoolProvidersState) => void;
}

const inputStyles = {
  input: {
    backgroundColor: '#161B22',
    border: '1px solid #374151',
    color: '#F9FAFB',
    minHeight: '28px',
    fontSize: '13px',
    textAlign: 'center'
  }
};

// Helper to render SVG icon from public folder with graceful fallback
const IconCell: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  if (err) return null;
  return (
    <img
      src={src}
      alt={alt}
      width={18}
      height={18}
      style={{ display: 'block' }}
      onError={() => setErr(true)}
    />
  );
};

const getDexIconSrc = (provider: string) => {
  const PUBLIC_URL = process.env.PUBLIC_URL || '';
  return `${PUBLIC_URL}/icons/dex/${provider}.svg`;
};

// Кастомный рендерер для опций с иконкой (Mantine v7)
const renderOption: SelectProps['renderOption'] = ({ option, checked }) => (
  <Group gap={8} align="center" style={{ width: '100%' }}>
    <IconCell src={getDexIconSrc(option.value)} alt={option.label} />
    <Text size="sm">{option.label}</Text>
    {checked && <span style={{ marginLeft: 'auto', color: '#4F46E5' }}>✓</span>}
  </Group>
);

export const PoolProvidersFilter: React.FC<PoolProvidersFilterProps> = ({ value, onChange }) => {
  const selectedProviders = Object.keys(value);
  const availableProviders = PROVIDERS.filter(p => !selectedProviders.includes(p));

  const handleAdd = (provider: string) => {
    onChange({
      ...value,
      [provider]: { min: null, max: null }
    });
  };

  const handleRemove = (provider: string) => {
    const newValue = { ...value };
    delete newValue[provider];
    onChange(newValue);
  };

  const handleMinChange = (provider: string, min: number | null) => {
    onChange({
      ...value,
      [provider]: { ...value[provider], min }
    });
  };
  const handleMaxChange = (provider: string, max: number | null) => {
    onChange({
      ...value,
      [provider]: { ...value[provider], max }
    });
  };

  return (
    <Paper p="sm" mb="sm" style={{ background: '#181C23', borderRadius: 8, border: '1px solid #23272F' }}>
      <Group mb={8} justify="space-between">
        <Group gap={6} align="center">
          <Text fw={600} size="sm">Pool Providers</Text>
          <ClickableHelpText
            text="Filter by pool provider"
            detailedInfo="Add one or more pool providers and set min/max trades for each. Leave min/max empty to disable filtering for that provider."
          />
        </Group>
        <Select
          placeholder="Add provider..."
          data={availableProviders.map(p => ({ value: p, label: p }))}
          onChange={v => v && handleAdd(v)}
          value={null}
          size="xs"
          style={{ minWidth: 160, textAlign: 'left' }}
          nothingFoundMessage="All added"
          renderOption={renderOption}
          styles={{
            input: {
              backgroundColor: '#21262D',
              border: '1px solid #374151',
              color: '#F9FAFB',
              minHeight: '28px',
              fontSize: '13px',
              textAlign: 'left'
            },
            dropdown: {
              backgroundColor: '#181C23',
              border: '1px solid #23272F'
            },
            option: {
              backgroundColor: '#181C23',
              color: '#F9FAFB',
              '&[dataSelected]': {
                backgroundColor: '#4F46E5'
              },
              '&:hover': {
                backgroundColor: '#21262D'
              }
            }
          }}
        />
      </Group>
      <div>
        {selectedProviders.length === 0 && (
          <Text size="xs" c="dimmed" mb={4}>No providers selected</Text>
        )}
        {selectedProviders.map((provider) => (
          <Group key={provider} mb={6} align="center" gap={8} style={{ flexWrap: 'nowrap' }}>
            {/* SVG icon for provider */}
            <span style={{ display: 'flex', alignItems: 'center', minWidth: 22 }}>
              <IconCell src={getDexIconSrc(provider)} alt={provider} />
            </span>
            <Text size="sm" style={{ minWidth: 70, textAlign: 'left' }}>{provider}</Text>
            <NumberInput
              placeholder="min"
              value={value[provider].min ?? ''}
              onChange={val => handleMinChange(provider, val === '' ? null : Number(val))}
              size="xs"
              min={0}
              max={1000}
              style={{ width: 70 }}
              hideControls 
              styles={{
                input: {
                  backgroundColor: '#161B22',
                  border: '1px solid #374151',
                  color: '#F9FAFB',
                  minHeight: '28px',
                  fontSize: '13px',
                  textAlign: 'center'
                } as React.CSSProperties
              }}
            />
            <NumberInput
              placeholder="max"
              value={value[provider].max ?? ''}
              onChange={val => handleMaxChange(provider, val === '' ? null : Number(val))}
              size="xs"
              min={0}
              max={1000}
              style={{ width: 70 }}
              hideControls 
              styles={{
                input: {
                  backgroundColor: '#161B22',
                  border: '1px solid #374151',
                  color: '#F9FAFB',
                  minHeight: '28px',
                  fontSize: '13px',
                  textAlign: 'center'
                } as React.CSSProperties
              }}
            />
            <Tooltip label="Remove provider" withArrow>
              <ActionIcon color="red" size="sm" variant="subtle" onClick={() => handleRemove(provider)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
      </div>
    </Paper>
  );
};

export default PoolProvidersFilter;
